import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import useCurrentShowroom from 'hooks/useCurrentShowroom';
import HalfImageHalfText from 'components/consumer/half-image-half-text';
import AdminPlaceholder from 'components/consumer/AdminPlaceholder';

const DynamicShowroomDetails = ({ config, isAdmin = false }) => {
  const {
    imageOrientation = 'Image Left',
    ctaLinkUrl = '/showrooms',
    ctaLinkText = 'View All Showrooms',
  } = config || {};

  const { showroom, loading, error } = useCurrentShowroom();

  const subTextContent = useMemo(() => {
    if (!showroom) return '';

    const {
      phone,
      street,
      city,
      state,
      zip,
      open_hours,
      email,
      parking_description,
    } = showroom;

    const hours =
      open_hours ||
      'Monday - Saturday: 10:00am - 6:00pm<br>Sunday: 12:00pm - 6:00pm';

    return `
      <b>Store Hours</b><br>
      ${hours}<br><br>

      <b>Phone:</b> ${phone || ''}<br><br>

      <b>Email:</b> ${
        email ? `<a href="mailto:${email}">${email}</a>` : ''
      }<br><br>

      <b>Location</b><br>
      ${street}, ${city}, ${state} ${zip}<br><br>

      ${
        parking_description
          ? `<b>Parking</b><br>${parking_description}<br><br>`
          : ''
      }
    `;
  }, [showroom]);

  const halfImageHalfTextConfig = useMemo(() => {
    if (!showroom) return null;

    return {
      imageProps: {
        src: showroom.image_url || 'https://joybird2.imgix.net/placeholder.jpg',
        alt: showroom.name,
        sizes: '50vw',
      },
      heading: {
        content: '',
        tagName: 'h2',
      },
      subText: {
        content: subTextContent,
      },
      imageOrientation: {
        value: imageOrientation,
        content: 'default',
      },
      toggleInputType: {
        selected: { value: 'CTALink' },
        ctaLink: {
          style: 'solid-button',
          color: 'brand',
          linkTo: ctaLinkUrl,
          content: ctaLinkText,
        },
      },
    };
  }, [showroom, subTextContent, imageOrientation, ctaLinkUrl, ctaLinkText]);

  if (isAdmin) {
    return (
      <AdminPlaceholder className="!min-h-[400px]">
        Dynamic Showroom Details
      </AdminPlaceholder>
    );
  }

  if (loading) {
    return (
      <div className="w-full py-12 text-center">
        <div className="text-gray-600">Loading showroom details...</div>
      </div>
    );
  }

  if (error || !showroom) {
    return null;
  }

  return <HalfImageHalfText config={halfImageHalfTextConfig} />;
};

DynamicShowroomDetails.propTypes = {
  config: PropTypes.object,
  isAdmin: PropTypes.bool,
};

export default DynamicShowroomDetails;
